/**
 * HttpError
 * ------------------------------------------------------------
 * フェッチ層で非 2xx (主に非 401) の HTTP レスポンスを統一的に表現するための Error クラス。
 * UI / ErrorBoundary / ログ / テストコードが status / body に直接アクセス可能になる。
 * body は JSON パースできた場合のみ格納され、それ以外は undefined。
 */
export class HttpError extends Error {
  public readonly status: number;
  public readonly body?: unknown;

  constructor(status: number, body?: unknown, message?: string) {
    super(message ?? 'HTTP ' + String(status));
    this.name = 'HttpError';
    this.status = status;
    this.body = body;
  }
}

/**
 * buildHttpError
 * ------------------------------------------------------------
 * 理由 (Why exists):
 *   - 各所で "throw new Error('HTTP '+status)" と書くと、
 *     1) status 情報の抽出が煩雑 2) body の取得処理が重複 3) 後で UI 改善したい時の差し替えが困難。
 *   - HttpError 生成と JSON パース(任意)を 1 箇所に集約し DRY と可観測性を向上。
 * 想定利用箇所 (Usage):
 *   - fetch 後: `if (!res.ok && res.status !== 401) throw await buildHttpError(res);`
 *   - リトライ後も同様。401 は別のリフレッシュハンドリングがあるので除外する想定。
 * clone する理由 (Why clone?):
 *   - Response 本体はストリームで一度 .json() / .text() を読むと消費される。
 *   - 呼び出し元が後段で res を使いたい可能性や、別のロガーで再度読みたい可能性を温存。
 *   - clone() は本体を複製し、安全に JSON パースを試みられる。
 * JSON パース try/catch, catch noop の理由:
 *   - エラーレスポンスが必ずしも JSON とは限らない（HTML / 空 / テキスト）。
 *   - パース失敗は付加情報が得られないだけで本質的エラーではないため黙殺。
 *   - ここで再 throw すると使用側で本来の HTTP status 情報が失われるリスクがある。
 * 設計判断:
 *   - JSON 以外を読む second 手段 (text()) は今は実装せずシンプルさ優先。
 *   - 必要になれば body 未取得時のみ text() フォールバックを追加できる。
 */
async function buildHttpError(res: Response): Promise<HttpError> {
  let data: unknown;
  try {
    // clone して JSON パースを試みる（失敗しても無視）
    data = await res.clone().json();
  } catch {
    // noop
  }
  return new HttpError(res.status, data);
}

/**
 * レスポンスが非 2xx の場合に HttpError を投げる。
 * @throws {HttpError}
 */
// Hono RPC クライアントの型上は成功レスポンスのみが現れ res.ok が常に true と推論されるため、
// Response 型に広げて受けることで、型検査と両立する実行時チェックとして成立させる。
export async function ensureResponseOk(res: Response): Promise<void> {
  if (!res.ok) throw await buildHttpError(res);
}

// default export を維持（関数名衝突を避けるため内部名 buildHttpError）
export default buildHttpError;
